async function visualize() {
  let isFirstRender = false
  let iframe
  let form
  let inputs = []
  let config = {}
  let currentTab = ''
  let isShowViewer = false
  let isConfiguratorLoaded = false
  let styleWarningHideTimer = null
  let styleWarningRoot = null
  let lastRestrictionsSignature = null
  // URL application guards
  const originalSearch = window.location.search || ''
  const originalParams = new URLSearchParams(originalSearch)
  // Consider incoming params only if URL has at least one configurator-related key
  const isRelevantParamKey = (key) => {
    // Treat common classic and door keys as relevant so we don't overwrite URL-provided values
    const exactKeys = new Set([
      // classic
      'color',
      'columnsCount',
      'materialRange',
      'luxeColor',
      'shelvesCount',
      // door
      'doors-style',
      'doorsColor',
      'doorsLuxeColor',
      'doorsMaterialRange',
      'edge-color',
      'edge-color-yes-no',
      'rail-width',
      'stile-width',
      'top-hinge-offset',
      'bottom-hinge-offset',
      'hinges-count',
      'dimensions-width',
      'dimensions-height',
      'dimensions-depth',
      'doors-openingDirection-1',
    ])
    if (exactKeys.has(key)) return true
    // Patterns
    if (key.startsWith('doors-openingDirection-')) return true
    if (key.startsWith('dimensions-')) return true
    if (key.startsWith('shelvesCount-')) return true
    return false
  }
  const hasIncomingParams = Array.from(originalParams.keys()).some(isRelevantParamKey)
  let initialUrlApplied = !hasIncomingParams

  // Wait for configurator/DPO markers to appear in the DOM (or time out)
  async function waitForConfiguratorMarkers(timeoutMs = 8000) {
    const strongSelectors = [
      '[visualizer-type]',
      'input[visualizer]',
      '.options-list[visualizer]',
      '.dpo-container .admin__field-option[class*="data-visualizer-value-"]',
      '.dpo-container [data-visualizer-value]'
    ]
    const weakSelectors = ['.dpo-container', '#itoris_dynamicproductoptions', '[data-dpo-root]']

    const hasStrong = () => strongSelectors.some((sel) => document.querySelector(sel))
    const hasWeak = () => weakSelectors.some((sel) => document.querySelector(sel))
    if (hasStrong()) return true

    return new Promise((resolve) => {
      const target = document.documentElement || document.body
      const observer = new MutationObserver(() => {
        if (!hasStrong()) return
        cleanup(true)
      })
      const timer = setTimeout(() => {
        cleanup(hasStrong())
      }, timeoutMs)
      const cleanup = (result) => {
        try { observer.disconnect() } catch (e) {}
        clearTimeout(timer)
        resolve(!!result)
      }
      if (hasWeak()) {
        observer.observe(target, { childList: true, subtree: true })
        return
      }
      observer.observe(target, { childList: true, subtree: true })
    })
  }

  // Remove visualizer UI when no configurator is assigned to the product
  function teardownVisualizerUI() {
    try {
      document.querySelectorAll('[visualizer-id="toggle-3d"], [visualizer-id="toggle-media"]').forEach((el) => el.remove())
      const wrapper = document.querySelector('#Slide-visualizer')
      if (wrapper && wrapper.parentNode) wrapper.parentNode.removeChild(wrapper)
      const wrappers = document.querySelectorAll('.wt-product__wrapper')
      wrappers.forEach((host) => host.classList.remove('wt-product__wrapper--visualizer', 'visualizer--active'))
      const products = document.querySelectorAll('.wt-product')
      products.forEach((p) => p.classList.remove('wt-product--visualizer', 'visualizer--active'))
    } catch (e) {
      console.warn('Visualizer teardown failed', e)
    }
  }

  // Cache Visualizer namespace and submodules once and avoid repeated window access
  const VisualizerNS = (typeof window !== 'undefined' && window.Visualizer) ? window.Visualizer : null
  const Validation = VisualizerNS && VisualizerNS.Validation ? VisualizerNS.Validation : null
  const HelpersNS = VisualizerNS && VisualizerNS.Helpers ? VisualizerNS.Helpers : null
  const Options = HelpersNS && HelpersNS.Options ? HelpersNS.Options : null
  const Message = VisualizerNS && VisualizerNS.Message ? VisualizerNS.Message : null
  const Form = HelpersNS && HelpersNS.Form ? HelpersNS.Form : null
  const Url = HelpersNS && HelpersNS.Url ? HelpersNS.Url : null

  if (!Validation) {
    throw new Error('Visualizer.Validation module is required but not found. Ensure visualizer.validation.js is loaded before visualizer.js')
  }
  if (!Options) {
    throw new Error('Visualizer.Helpers.Options module is required but not found. Ensure visualizer.options.js is loaded before visualizer.js')
  }
  if (!Message) {
    throw new Error('Visualizer.Message module is required but not found. Ensure visualizer.message.js is loaded before visualizer.js')
  }
  if (!Form) {
    throw new Error('Visualizer.Helpers.Form module is required but not found. Ensure visualizer.form.js is loaded before visualizer.js')
  }
  if (!Url) {
    throw new Error('Visualizer.Helpers.Url module is required but not found. Ensure visualizer.url.js is loaded before visualizer.js')
  }

  async function setFromUrl() {
    await Url.setFromUrl({ hasIncomingParams, originalParams, config, inputs })
    // Mark that URL params have been applied so we can safely start saving back to URL
    initialUrlApplied = true
  }


  function debounce(callee, timeoutMs) {
    return function perform(...args) {
      let previousCall = this.lastCall

      this.lastCall = Date.now()

      if (previousCall && this.lastCall - previousCall <= timeoutMs) {
        clearTimeout(this.lastCallTimer)
      }

      this.lastCallTimer = setTimeout(() => callee(...args), timeoutMs)
    }
  }


  // Form helpers moved to Visualizer.Helpers.Form
  function collectFormData(form) {
    const result = Form.getFormData(form, inputs, config)
    if (!result) return inputs
    if (result.addedCount > 0) {
      isFirstRender = true
    }
    inputs = result.inputs
    return inputs
  }

  function normalizeConfiguratorType(rawType) {
    const normalized = rawType == null ? '' : String(rawType).trim().toLowerCase()
    if (!normalized) return 'classic'
    const aliases = {
      bookcases: 'bookcases',
      carcasses: 'carcasses',
      wallunit: 'wall-unit',
      wall_unit: 'wall-unit',
      'wall-unit': 'wall-unit',
    }
    return aliases[normalized] || normalized
  }

  function getCurrentConfigType() {
    try {
      const V = (typeof window !== 'undefined' && window.Visualizer) ? window.Visualizer : {}
      const raw = (V && typeof V.getConfigType === 'function') ? V.getConfigType() : (V?.isDoor ? 'door' : 'classic')
      return normalizeConfiguratorType(raw)
    } catch (e) {
      return 'classic'
    }
  }

  function isSlimlineFrameStyle() {
    try {
      const cfgVal = config?.frameStyle
      const normalize = (v) => (v == null ? '' : String(v).toLowerCase().trim())
      const matchesCfg = Array.isArray(cfgVal)
        ? cfgVal.map(normalize).includes('slimline')
        : normalize(cfgVal) === 'slimline'
      if (matchesCfg) return true
      const list = document.querySelector('.options-list[visualizer="frameStyle"]')
      if (!list) return false
      const checked = list.querySelector('input[type="radio"]:checked')
      if (!checked) return false
      const wrap = checked.closest('.admin__field-option')
      if (wrap && wrap.classList.contains('data-visualizer-value-slimline')) return true
      const value = normalize(checked.value)
      return value === 'slimline'
    } catch (err) {
      return false
    }
  }

  // For Slimline: keep countertop (as top) with overhang options and disable inner top panel
  function applySlimlineCountertopVisibility() {
    try {
      const isSlimline = isSlimlineFrameStyle()
      const setHiddenValue = (key, value) => {
        try {
          if (typeof Options?._setHiddenVisualizerValue === 'function') {
            Options._setHiddenVisualizerValue(key, value, config)
          } else if (value === undefined) {
            delete config[key]
          } else {
            config[key] = value
          }
        } catch (e) { /* non-fatal */ }
      }

      if (isSlimline) {
        setHiddenValue('top-panel-enabled', false)
        setHiddenValue('countertopColor', 'null') // selects data-visualizer-value-null
      } else {
        setHiddenValue('top-panel-enabled', undefined)
      }
    } catch (errOverhang) {
      console.warn('Failed to toggle countertop overhang visibility:', errOverhang)
    }
  }

  function getStyleWarningDefaultText(reason) {
    const normalized = reason == null ? '' : String(reason).trim().toLowerCase()
    if (normalized === 'no-fronts') {
      return 'This style is not applicable to the current configuration because there are no doors or drawers'
    }
    if (normalized === 'small-drawers-only-flat') {
      return 'Some fronts are too small for this style and will remain flat'
    }
    if (normalized === 'mixed-columns') {
      return 'This style can only be applied to some columns in the current configuration'
    }
    return 'This style is not fully applicable to the current configuration'
  }

  function buildStyleWarningText(payload = {}) {
    const baseText = payload.message ? String(payload.message) : getStyleWarningDefaultText(payload.reason)
    const affectedColumns = Array.isArray(payload.affectedColumns)
      ? payload.affectedColumns
          .map((v) => String(v).trim())
          .filter(Boolean)
      : []
    if (affectedColumns.length === 0) return baseText
    return `${baseText}. Affected columns: ${affectedColumns.join(', ')}`
  }

  function ensureStyleWarningNode() {
    if (styleWarningRoot && document.body && document.body.contains(styleWarningRoot)) return styleWarningRoot
    if (!document.body) return null

    const root = document.createElement('div')
    root.className = 'visualizer-style-warning'
    root.setAttribute('role', 'status')
    root.setAttribute('aria-live', 'polite')
    root.innerHTML = `
      <div class="visualizer-style-warning__content">
        <div class="visualizer-style-warning__icon-wrap" aria-hidden="true">
          <svg class="visualizer-style-warning__icon" viewBox="0 0 24 24" fill="none">
            <path d="M12 3L22 21H2L12 3Z" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round"></path>
            <path d="M12 8V13" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"></path>
            <circle cx="12" cy="17" r="1.1" fill="currentColor"></circle>
          </svg>
        </div>
        <div class="visualizer-style-warning__text-wrap">
          <div class="visualizer-style-warning__text"></div>
        </div>
        <div class="visualizer-style-warning__close-slot">
          <button
            type="button"
            class="visualizer-style-warning__close"
            aria-label="Close warning"
            data-icon-position="Only"
            data-show-description="false"
            data-show-icon="true"
            data-show-text="false"
            data-size="Medium"
            data-state="Default"
            data-status-indicator="true"
            data-type="Transperent"
          >
            <span class="visualizer-style-warning__close-icon-wrap" aria-hidden="true">
              <svg class="visualizer-style-warning__close-icon" viewBox="0 0 13 13" fill="none">
                <path d="M2 2L11 11M11 2L2 11" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"></path>
              </svg>
            </span>
          </button>
        </div>
      </div>
    `
    const closeBtn = root.querySelector('.visualizer-style-warning__close')
    if (closeBtn) {
      closeBtn.addEventListener('click', () => hideStyleWarning())
    }
    document.body.appendChild(root)
    styleWarningRoot = root
    return root
  }

  function hideStyleWarning() {
    if (styleWarningHideTimer) {
      clearTimeout(styleWarningHideTimer)
      styleWarningHideTimer = null
    }
    if (!styleWarningRoot) return
    styleWarningRoot.classList.remove('visualizer-style-warning--visible')
  }

  function showStyleWarning(payload = {}) {
    const root = ensureStyleWarningNode()
    if (!root) return
    const textNode = root.querySelector('.visualizer-style-warning__text')
    if (textNode) textNode.textContent = buildStyleWarningText(payload)
    root.classList.add('visualizer-style-warning--visible')

    if (styleWarningHideTimer) clearTimeout(styleWarningHideTimer)
    const ttl = Number.isFinite(Number(payload.timeoutMs)) ? Number(payload.timeoutMs) : 5000
    styleWarningHideTimer = setTimeout(() => {
      hideStyleWarning()
    }, Math.max(1500, ttl))
  }

  function normalizeFrontStyleToken(value) {
    return value == null ? '' : String(value).trim().toLowerCase()
  }

  function getFrontStyleTokens(wrap, input) {
    const tokens = []
    if (wrap) {
      const cls = Array.from(wrap.classList || []).find((c) => c.startsWith('data-visualizer-value-'))
      if (cls) tokens.push(cls.replace('data-visualizer-value-', ''))
    }
    if (input && input.value != null) tokens.push(String(input.value))
    return tokens.map(normalizeFrontStyleToken).filter(Boolean)
  }

  function resolveSelectedFrontStyleSupport() {
    const list = document.querySelector('.options-list[visualizer="doors-style"]')
    if (!list) return null
    const selectedInput = list.querySelector('input[type="radio"]:checked')
    if (!selectedInput) return null
    const wrap = selectedInput.closest('.admin__field-option')
    const tokens = getFrontStyleTokens(wrap, selectedInput)
    if (!tokens.length) return null
    const supportByStyle = config?.__frontStyleRestriction?.supportByStyle
    if (!supportByStyle || typeof supportByStyle !== 'object') return null
    for (const token of tokens) {
      if (supportByStyle[token]) {
        return supportByStyle[token]
      }
    }
    return null
  }

  function maybeShowFrontStyleSupportWarning() {
    const support = resolveSelectedFrontStyleSupport()
    if (!support) {
      hideStyleWarning()
      return
    }
    const supportLevel = normalizeFrontStyleToken(support.supportLevel) || 'full'
    if (supportLevel !== 'partial' && supportLevel !== 'none') {
      hideStyleWarning()
      return
    }
    const reason = supportLevel === 'partial' ? 'small-drawers-only-flat' : 'no-fronts'
    showStyleWarning({
      reason: reason,
    })
  }

  function bindFrontStyleWarningOnChange() {
    if (window.__vizFrontStyleWarningBound) return
    const root = document.querySelector('#itoris_dynamicproductoptions') || document
    const styleListSelector = '.options-list[visualizer="doors-style"]'
    root.addEventListener('change', (event) => {
      const target = event && event.target
      if (!target || !(target instanceof Element)) return
      if (!target.matches('input[type="radio"]')) return
      const list = target.closest(styleListSelector)
      if (!list) return
      maybeShowFrontStyleSupportWarning()
    }, true)
    window.__vizFrontStyleWarningBound = true
  }

  function maybeShowHiddenHandlesWarning() {
    if (!config.__hasHiddenHandles) return
    showStyleWarning({
      message: 'Some fronts are too small for the selected handles.',
    })
  }

  function maybeShowHiddenHandlesWarningFromRestrictions() {
    const shouldCheck = config.__pendingHiddenHandlesSelection === true
    if (!shouldCheck) return
    config.__pendingHiddenHandlesSelection = false
    if (!config.__hasHiddenHandles) return
    maybeShowHiddenHandlesWarning()
  }

  function bindHandleStyleWarningOnChange() {
    if (window.__vizHandleStyleWarningBound) return
    const root = document.querySelector('#itoris_dynamicproductoptions') || document
    const handleListSelector = '.options-list[visualizer="handles-code"]'
    root.addEventListener('change', (event) => {
      const target = event && event.target
      if (!target || !(target instanceof Element)) return
      if (!target.matches('input[type="radio"]')) return
      const list = target.closest(handleListSelector)
      if (!list) return
      const wrap = target.closest('.admin__field-option')
      if (!wrap || !list.contains(wrap)) return
      hideStyleWarning()
      config.__pendingHiddenHandlesSelection = true
    }, true)
    window.__vizHandleStyleWarningBound = true
  }

  function restoreDoorConstructionStateFromConfig() {
    try {
      if (typeof Options?.updateColumnInterfaceVisibility === 'function') {
        Options.updateColumnInterfaceVisibility(config)
      }
      const Form = global.Visualizer && global.Visualizer.Helpers && global.Visualizer.Helpers.Form
      if (!Form) return
      if (typeof Form.reapplyOptionsFromConfig === 'function') {
        Form.reapplyOptionsFromConfig(config, document, { onlyColumnScoped: true })
        Form.reapplyOptionsFromConfig(config, document, { onlyVisible: true })
      }
      if (typeof Form.reapplyScalarInputsFromConfig === 'function') {
        Form.reapplyScalarInputsFromConfig(config, document, { onlyColumnScoped: true })
        Form.reapplyScalarInputsFromConfig(config, document, { onlyVisible: true })
      }
    } catch (restoreErr) {
      console.warn('Failed to restore door construction state from config', restoreErr)
    }
  }

  function scheduleRestoreDoorConstructionStateFromConfig() {
    restoreDoorConstructionStateFromConfig()
    if (typeof requestAnimationFrame === 'function') {
      requestAnimationFrame(restoreDoorConstructionStateFromConfig)
    }
    ;[0, 50, 120, 300, 600].forEach((ms) => setTimeout(restoreDoorConstructionStateFromConfig, ms))
  }

  async function render() {
    form = document.querySelector('.dpo-container')
    inputs = collectFormData(form)
    if (isFirstRender) {
      await setFromUrl()
      isFirstRender = false
    }
    applySlimlineCountertopVisibility()
    if (!iframe) {
      iframe = document.querySelector('#visualizer-iframe')
      if (!iframe) return
    }
    if (currentTab === 'doorConstruction') {
      restoreDoorConstructionStateFromConfig()
    }
    if (isShowViewer) Message.post(iframe, config, currentTab, Validation, isConfiguratorLoaded)
    // After any render-driven DOM changes, ensure hidden checked options are auto-corrected
    Options.enforceOptionsVisibility(config)
    if (currentTab === 'doorConstruction') {
      restoreDoorConstructionStateFromConfig()
    }
    const type = getCurrentConfigType()
    if (typeof Options?.arrangeDoorOpeningGroups === 'function' && type !== 'bookcases' && type !== 'carcasses') Options.arrangeDoorOpeningGroups(config)
    if (typeof Options?.makeDoorOpeningDirectionToggles === 'function') Options.makeDoorOpeningDirectionToggles(config)
  }

  let mobileFixedActive = false
  let mobileOriginalTop = null
  let mobileSpacerEl = null
  let mobileOriginalPaddingTop = ''
  const isMobileViewport = () => {
    try {
      return window.matchMedia('(max-width: 749px) and (hover: none)').matches
    } catch (e) {
      return window.innerWidth <= 749
    }
  }

  const scheduleNextFrame = (cb) => {
    if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
      window.requestAnimationFrame(cb)
    } else {
      setTimeout(cb, 0)
    }
  }

  const getMobileWrapper = () => document.querySelector('#Slide-visualizer')

  function ensureMobileSpacer(wrapper) {
    if (!wrapper || !wrapper.parentNode) return null
    if (!mobileSpacerEl) {
      mobileSpacerEl = document.createElement('div')
      mobileSpacerEl.className = 'visualizer__mobile-spacer'
      mobileSpacerEl.setAttribute('aria-hidden', 'true')
    }
    if (wrapper.nextSibling !== mobileSpacerEl) {
      wrapper.parentNode.insertBefore(mobileSpacerEl, wrapper.nextSibling)
    }
    return mobileSpacerEl
  }

  function removeMobileSpacer() {
    if (mobileSpacerEl && mobileSpacerEl.parentNode) {
      mobileSpacerEl.parentNode.removeChild(mobileSpacerEl)
    }
    if (mobileSpacerEl) {
      mobileSpacerEl.style.display = 'none'
      mobileSpacerEl.style.height = ''
    }
  }

  function updateMobileOriginalTop() {
    const anchor = (mobileSpacerEl && mobileSpacerEl.isConnected) ? mobileSpacerEl : getMobileWrapper()
    if (!anchor) return
    const rect = anchor.getBoundingClientRect()
    mobileOriginalTop = rect.top + (window.scrollY || window.pageYOffset || 0)
  }

  function enableMobileFixed() {
    if (!isMobileViewport() || mobileFixedActive) return
    const wrapper = getMobileWrapper()
    if (!wrapper) return

    const rect = wrapper.getBoundingClientRect()
    const measuredWidth = rect.width || wrapper.offsetWidth || wrapper.clientWidth
    const measuredHeight = rect.height || wrapper.offsetHeight || wrapper.clientHeight
    const computedStyles = (typeof window !== 'undefined' && window.getComputedStyle) ? window.getComputedStyle(wrapper) : null
    const spacer = ensureMobileSpacer(wrapper)
    if (spacer) {
      spacer.style.display = 'block'
      spacer.style.height = measuredHeight ? `${measuredHeight}px` : ''
    }

    if (!mobileOriginalPaddingTop) {
      const inlinePadding = wrapper.style.paddingTop || ''
      const computedPadding = computedStyles ? (computedStyles.paddingTop || '') : ''
      mobileOriginalPaddingTop = inlinePadding || computedPadding || ''
    }

    wrapper.style.position = 'fixed'
    wrapper.style.top = '0'
    wrapper.style.left = '0'
    wrapper.style.right = '0'
    wrapper.style.width = '100vw'
    wrapper.style.maxWidth = '100vw'
    wrapper.style.minWidth = '100vw'
    wrapper.style.marginLeft = '0'
    wrapper.style.marginRight = '0'
    wrapper.style.paddingLeft = '0'
    wrapper.style.paddingRight = '0'
    wrapper.style.paddingTop = '0'
    wrapper.style.height = measuredHeight ? `${measuredHeight}px` : ''
    wrapper.style.zIndex = '10000'
    wrapper.classList.add('visualizer__fixed')
    void wrapper.offsetWidth
    if (wrapper.style.width !== '100vw') {
      wrapper.style.width = '100vw'
      wrapper.style.maxWidth = '100vw'
      wrapper.style.minWidth = '100vw'
    }
    mobileFixedActive = true
    mobileOriginalTop = null
    updateMobileOriginalTop()
  }

  function disableMobileFixed() {
    if (!mobileFixedActive) return
    const wrapper = getMobileWrapper()
    if (wrapper) {
      wrapper.style.position = ''
      wrapper.style.top = ''
      wrapper.style.left = ''
      wrapper.style.right = ''
      wrapper.style.width = ''
      wrapper.style.height = ''
      wrapper.style.paddingTop = mobileOriginalPaddingTop || ''
      wrapper.style.zIndex = ''
      wrapper.classList.remove('visualizer__fixed')
    }
    mobileFixedActive = false
    mobileOriginalPaddingTop = ''
    removeMobileSpacer()
    mobileOriginalTop = null
    scheduleNextFrame(() => updateMobileOriginalTop())
  }

  function ensureColumnsCountSelection() {
    try {
      if (typeof Options?.enforceRequiredRadios !== 'function') return
      const attempt = () => {
        const list = document.querySelector('.options-list[visualizer="columnsCount"]')
        if (!list) return false
        Options.enforceRequiredRadios('columnsCount', config)
        const hasChecked = !!list.querySelector('input[type="radio"]:checked')
        return hasChecked
      }
      attempt()
    } catch (e) {
      console.warn('ensureColumnsCountSelection failed:', e)
    }
  }

  function handleMobileScroll() {
    if (!isMobileViewport()) {
      if (mobileFixedActive) disableMobileFixed()
      return
    }
    const wrapper = getMobileWrapper()
    if (!wrapper || wrapper.classList.contains('visualizer__wrapper_hidden')) {
      if (mobileFixedActive) disableMobileFixed()
      mobileOriginalTop = null
      return
    }

    const currentScroll = window.scrollY || window.pageYOffset || 0
    if (mobileOriginalTop == null) updateMobileOriginalTop()
    if (mobileOriginalTop == null) return

    if (!mobileFixedActive && currentScroll >= mobileOriginalTop) {
      enableMobileFixed()
    } else if (mobileFixedActive && currentScroll < mobileOriginalTop) {
      disableMobileFixed()
    }
  }

  function mountButtons() {
    const showVisualizer = () => {
      const hiddenEls = document.querySelectorAll('[visualizer-id="hidden"]')
      hiddenEls.forEach(item => item.classList.add('visualizer__hidden'))
      const wrapper = document.querySelector('#Slide-visualizer')
      if (wrapper) wrapper.classList.remove('visualizer__wrapper_hidden')
      isShowViewer = true
      const host = wrapper ? wrapper.closest('.wt-product__wrapper') : null
      if (host) host.classList.add('visualizer--active')
      // post an update if config is ready
      if (Validation.isConfigReady(config, isConfiguratorLoaded)) Message.post(iframe, config, currentTab, Validation, isConfiguratorLoaded)
    }
    const showMediaGallery = () => {
      const wrapper = document.querySelector('#Slide-visualizer')
      if (wrapper) wrapper.classList.add('visualizer__wrapper_hidden')
      const hiddenEls = document.querySelectorAll('[visualizer-id="hidden"]')
      hiddenEls.forEach(item => item.classList.remove('visualizer__hidden'))
      isShowViewer = false
      const host = wrapper ? wrapper.closest('.wt-product__wrapper') : null
      if (host) host.classList.remove('visualizer--active')
    }

    const toggles3d = document.querySelectorAll('[visualizer-id="toggle-3d"]')
    toggles3d.forEach(item => {
      item.addEventListener('click', () => {
        showVisualizer()
      })
    })
    const toggleMedia = document.querySelector('[visualizer-id="toggle-media"]')
    if (toggleMedia) toggleMedia.addEventListener('click', () => { showMediaGallery() })

    // Thumbnails behavior: first thumbnail -> show visualizer, others -> show media gallery
    const thumbnailsLists = document.querySelectorAll('[id^="Slider-Thumbnails-"]')
    thumbnailsLists.forEach((list) => {
      const items = Array.from(list.querySelectorAll('.thumbnail-list__item'))
      items.forEach((li, idx) => {
        const btn = li.querySelector('button.thumbnail')
        if (!btn) return
        // Avoid multiple binding
        if (btn.__visualizerBound) return
        btn.__visualizerBound = true
        btn.addEventListener('click', () => {
          if (idx === 0) {
            showVisualizer()
          } else {
            showMediaGallery()
          }
        })
      })
    })
  }

  function listenChangeTabs() {
    const wrapper = document.querySelector('#itoris_dynamicproductoptions')
    if (!wrapper) return
    wrapper.addEventListener('change-tab', (event) => {
      currentTab = event.detail.tab
      const modeByTab = {
        cabinetDimensions: 'respectUser',
        doorConstruction: 'forceShow',
        typeOfBase: 'respectUser',
        materials: 'respectUser',
        handles: 'forceShow',
        counterTop: 'respectUser',
        modifications: 'forceHide',
      }
      const mode = modeByTab[currentTab] || 'respectUser'
      Message.postDoorsDisplayMode(iframe, mode)
      // After tab switch, re-apply saved selections to the now-visible section
      try {
        if (currentTab === 'doorConstruction') {
          scheduleRestoreDoorConstructionStateFromConfig()
        } else {
          if (typeof Options?.updateColumnInterfaceVisibility === 'function') {
            Options.updateColumnInterfaceVisibility(config)
          }
          if (typeof Form?.reapplyOptionsFromConfig === 'function') {
            Form.reapplyOptionsFromConfig(config, document, { onlyVisible: true })
          }
        }
      } catch (reapplyErr) {
        console.warn('Failed to reapply options after tab change', reapplyErr)
      }
    })
  }

  function applyConfigurableBorderRestrictionsClassic(payload, config) {
    try {
      if (!payload?.fronts?.configurableBorder) return
      const cfgBorder = payload.fronts.configurableBorder
      const available = !!cfgBorder.available
      const stileRange = cfgBorder.stileWidth
      const railRange = cfgBorder.railWidth

      if (typeof Options?.setDoorSupportsConfigurableBorder === 'function' && available !== undefined) {
        Options.setDoorSupportsConfigurableBorder(available, config)
      }

      if (available !== undefined && config) {
        config.__supportsConfigurableBorder = available
        if (!available) {
          delete config.__configurableStileWidthRange
          delete config.__configurableRailWidthRange
        }
      }

      if (available === false) return

      if (typeof Options?.setDoorConfigurableWidthRange === 'function' && (stileRange || railRange)) {
        Options.setDoorConfigurableWidthRange(stileRange, railRange, config)
      }
      if (config) {
        if (stileRange) config.__configurableStileWidthRange = stileRange
        if (railRange) config.__configurableRailWidthRange = railRange
      }
    } catch (err) {
      console.warn('Failed to apply configurable border restrictions (classic):', err)
    }
  }

  function applyConfigurableBorderRestrictionsDoor(doorsPayload, config) {
    try {
      if (!doorsPayload || typeof doorsPayload !== 'object') return
      const cfgBorder = doorsPayload.configurableBorder
      const hasNewShape = cfgBorder && typeof cfgBorder === 'object' && Object.prototype.hasOwnProperty.call(cfgBorder, 'available')
      const available = hasNewShape
        ? !!cfgBorder.available
        : (Object.prototype.hasOwnProperty.call(doorsPayload, 'supportsConfigurableBorder') ? !!doorsPayload.supportsConfigurableBorder : undefined)
      const stileRange = hasNewShape ? cfgBorder?.stileWidth : doorsPayload?.configurableStileWidth
      const railRange = hasNewShape ? cfgBorder?.railWidth : doorsPayload?.configurableRailWidth

      if (typeof Options?.setDoorSupportsConfigurableBorder === 'function' && available !== undefined) {
        Options.setDoorSupportsConfigurableBorder(available, config)
      }

      if (available !== undefined && config) {
        config.__supportsConfigurableBorder = available
        if (!available) {
          delete config.__configurableStileWidthRange
          delete config.__configurableRailWidthRange
        }
      }

      if (available === false) return

      if (typeof Options?.setDoorConfigurableWidthRange === 'function' && (stileRange || railRange)) {
        Options.setDoorConfigurableWidthRange(stileRange, railRange, config)
      }
      if (config) {
        if (stileRange) config.__configurableStileWidthRange = stileRange
        if (railRange) config.__configurableRailWidthRange = railRange
      }
    } catch (err) {
      console.warn('Failed to apply configurable border restrictions (door):', err)
    }
  }

  // Split restrictions handlers by configurator type for clarity and maintainability
  function handleRestrictionsChangedClassic(payload, config) {
    // Classic restrictions handling
    console.log('handleRestrictionsChangedClassic', payload);
    try {
      if (typeof Options?.setCableHoleOptionsVisibility === 'function') {
        Options.setCableHoleOptionsVisibility(!!payload.canFitCableHole, config)
      }
    } catch (err2) {
      console.warn('Failed to apply cable hole visibility changes:', err2)
    }
    try {
      if (typeof Options?.setDoorOpeningDirectionsAllowed === 'function') {
        Options.setDoorOpeningDirectionsAllowed(payload.allowedOpeningDirections, config)
      }
    } catch (err4) {
      console.warn('Failed to apply opening directions changes:', err4)
    }

    // New: Apply door-specific restrictions for cupboard classic as well
    const doorsPayload = payload.doors;
    if (doorsPayload) {
      try {
        if (typeof Options?.setDoorMaxHingesCount === 'function' && doorsPayload.maxHingesCount != null) {
          Options.setDoorMaxHingesCount(doorsPayload.maxHingesCount, config)
        }
      } catch (errH) {
        console.warn('Failed to apply door max hinges count (classic):', errH)
      }
      try {
        if (typeof Options?.setDoorMaxHingeOffsets === 'function' && (doorsPayload.maxTopHingeOffset != null || doorsPayload.maxBottomHingeOffset != null)) {
          Options.setDoorMaxHingeOffsets(doorsPayload.maxTopHingeOffset, doorsPayload.maxBottomHingeOffset, config)
        }
      } catch (errOff) {
        console.warn('Failed to apply door hinge offsets limits (classic):', errOff)
      }
    }
    if (payload.fronts) {
      try {
        config.__hasHiddenHandles = payload.fronts.hasHiddenHandles === true
        maybeShowHiddenHandlesWarningFromRestrictions()
      } catch (persistHH) {
        /* non-fatal */
      }
      try {
        applyConfigurableBorderRestrictionsClassic(payload, config)
      } catch (errCB) {
        console.warn('Failed to apply configurable border support (classic):', errCB)
      }
      try {
        const styleSupport = payload.fronts.allowedStyles
        if (typeof Options?.setFrontStylesAvailability === 'function' && styleSupport) {
          Options.setFrontStylesAvailability({
            allowedStyles: styleSupport
          }, config)
        }
      } catch (errFrontsStyles) {
        console.warn('Failed to apply front styles restrictions:', errFrontsStyles)
      }
    }
    try {
      if (Array.isArray(payload.columns) && payload.columns.length > 0 && config) {
        config.columnsCount = String(payload.columns.length)
      }
      if (Array.isArray(payload.columns) && typeof Options?.setColumnRangeLimits === 'function') {
        Options.setColumnRangeLimits(payload.columns, config)
      }
    } catch (errColumns) {
      console.warn('Failed to apply per-column range limits:', errColumns)
    }
    try {
      if (payload.allowedColumnTemplates && typeof Options?.setAllowedColumnTemplates === 'function') {
        Options.setAllowedColumnTemplates(payload.allowedColumnTemplates, config)
      }
    } catch (errTemplates) {
      console.warn('Failed to apply allowed column templates:', errTemplates)
    }
    try {
      if (payload.skirtingCutout && typeof Options?.setSkirtingCutoutLimits === 'function') {
        Options.setSkirtingCutoutLimits(payload.skirtingCutout, config)
      }
    } catch (errSC) {
      console.warn('Failed to apply skirting cutout limits:', errSC)
    }
    try {
      if (payload.countertop?.supportsOverhang && typeof Options?.setCountertopOverhangSupport === 'function') {
        Options.setCountertopOverhangSupport(payload.countertop.supportsOverhang, config)
      }
    } catch (errCountertop) {
      console.warn('Failed to apply countertop overhang support:', errCountertop)
    }
    try {
      if (typeof Options?.setRunnerMetrics === 'function') {
        Options.setRunnerMetrics(payload.runners, config)
      }
    } catch (errRunners) {
      console.warn('Failed to update runner hidden fields:', errRunners)
    }
    try {
      if (typeof Options?.updateColumnInterfaceVisibility === 'function') {
        Options.updateColumnInterfaceVisibility(config)
      }
    } catch (errColumnVisibility) {
      console.warn('Failed to sync column interface visibility after restrictions:', errColumnVisibility)
    }
    try {
      const Form = global.Visualizer && global.Visualizer.Helpers && global.Visualizer.Helpers.Form
      if (Form && typeof Form.reapplyScalarInputsFromConfig === 'function') {
        Form.reapplyScalarInputsFromConfig(config, document, { onlyColumnScoped: true })
      }
      if (Form && typeof Form.reapplyOptionsFromConfig === 'function' && config['doors-style'] != null) {
        Form.reapplyOptionsFromConfig(config, document)
      }
    } catch (errReapplyAfterRestrictions) {
      console.warn('Failed to reapply config after restrictions:', errReapplyAfterRestrictions)
    }
  }

  function handleRestrictionsChangedDoor(payload, config) {
    // Door-specific restrictions handling
    try {
      if (typeof Options?.setDoorMaxHingesCount === 'function' && payload.maxHingesCount != null) {
        Options.setDoorMaxHingesCount(payload.maxHingesCount, config)
      }
    } catch (errH) {
      console.warn('Failed to apply door max hinges count:', errH)
    }
    try {
      if (typeof Options?.setDoorMaxHingeOffsets === 'function' && (payload.maxTopHingeOffset != null || payload.maxBottomHingeOffset != null)) {
        Options.setDoorMaxHingeOffsets(payload.maxTopHingeOffset, payload.maxBottomHingeOffset, config)
      }
    } catch (errOff) {
      console.warn('Failed to apply door hinge offsets limits:', errOff)
    }
    try {
      applyConfigurableBorderRestrictionsDoor(payload, config)
    } catch (errCB) {
      console.warn('Failed to apply configurable border support (door):', errCB)
    }
    try {
      if (payload.skirtingCutout && typeof Options?.setSkirtingCutoutLimits === 'function') {
        Options.setSkirtingCutoutLimits(payload.skirtingCutout, config)
      }
    } catch (errSC) {
      console.warn('Failed to apply cut-out limits (door):', errSC)
    }
  }

  function isRestrictionsChangedEvent(payload) {
    const typeVal = payload?.type
    const normalized = typeof typeVal === 'string' ? typeVal.toLowerCase() : ''
    return normalized === 'restrictionschanged' || normalized === 'restrictions_changed'
  }

  function subscribeRestrictionsChanged() {
    if (window.__visualizerRestrictionsSubscribed) return
    const handler = (e) => {
      try {
        const dynamicOrigin = (() => { try { return new URL(targetSrc).origin } catch (_) { return null } })()
        const allowed = new Set([
          'https://tcs-configurator.pages.dev',
          'https://cabinet-shop.dev.web3dev.group',
          'https://v-0-4.cabinet-shop.dev.web3dev.group',
          'https://cabinet-shop.release.web3dev.group',
          dynamicOrigin
        ].filter(Boolean))
        if (!allowed.has(e.origin)) return
        const payload = e.data && e.data.payload
        if (!payload || typeof payload !== 'object') return
        if (isRestrictionsChangedEvent(payload)) {
          console.log('restrictionsChanged', payload)
          let signature = null
          try {
            signature = JSON.stringify(payload)
          } catch (sigErr) {
            signature = null
          }
          const hasPendingHandleSelection = config.__pendingHiddenHandlesSelection === true
          if (signature && signature === lastRestrictionsSignature && !hasPendingHandleSelection) return
          if (signature) lastRestrictionsSignature = signature
          const V = (typeof window !== 'undefined' && window.Visualizer) ? window.Visualizer : {}
          const type = (V && typeof V.getConfigType === 'function') ? V.getConfigType() : (V?.isDoor ? 'door' : 'classic')
          if (type === 'door') {
            handleRestrictionsChangedDoor(payload, config)
          } else {
            handleRestrictionsChangedClassic(payload, config)
          }
        }
      } catch (err) {
        console.warn('Failed handling restrictionsChanged message:', err)
      }
    }
    window.addEventListener('message', handler)
    window.__visualizerRestrictionsSubscribed = true
  }

  // Show buttons only after configurator is confirmed
  const showButtons = () => {
    document.querySelectorAll('[visualizer-id="toggle-3d"], [visualizer-id="toggle-media"]').forEach((el) => {
      el.classList.add('visualizer__toggle--visible')
    })
  }

  async function mount() {
    // Wait specifically for the DPO marker or container to appear (more reliable for door template)
    await new Promise((res) => {
      const doorSelector = '#options_1001_text[visualizer-type="door"], input[visualizer-type="door"]'
      const isReady = () => {
        // Consider ready if marker exists OR basic DPO container structure exists
        if (document.querySelector(doorSelector)) return true
        const c = document.querySelector('.dpo-container')
        if (!c) return false
        // Be less strict here to avoid missing minimal door template
        const hasAnyOptions = c.querySelector('.options-list') || c.querySelector('.admin__field-option')
        return !!hasAnyOptions
      }
      if (isReady()) return res(true)
      const obs = new MutationObserver(() => { if (isReady()) { try { obs.disconnect() } catch(e){}; res(true) } })
      obs.observe(document.body, { childList: true, subtree: true })
      setTimeout(() => { try { obs.disconnect() } catch(e){}; res(true) }, 15000)
    })

    // Detect configurator type with retries until found or timeout
    try {
      const selector = '[visualizer-type]'
      let el = document.querySelector(selector)
      if (!el) {
        // short polling window to catch late injection
        const start = Date.now()
        const maxMs = 5000
        while (!el && Date.now() - start < maxMs) {
          await new Promise(r => setTimeout(r, 100))
          el = document.querySelector(selector)
        }
      }
      const typeAttr = el && (el.getAttribute('visualizer-type') || el.getAttribute('visualizer_type'))
      const configType = normalizeConfiguratorType(typeAttr)
      console.log('detected configurator type:', typeAttr)
      window.Visualizer = window.Visualizer || {}
      window.Visualizer.configType = configType
      // Backward compatibility for older checks
      window.Visualizer.isDoor = configType === 'door'
      console.log('configuratorType', configType)
    } catch (e) {}

    // Ensure iframe exists (create without src to attach listeners first)
    let iframeEl = document.querySelector('#visualizer-iframe')
    if (!iframeEl) {
      const slot = document.querySelector('#visualizer-iframe-slot') || document.querySelector('#Slide-visualizer')
      if (slot) {
        const el = document.createElement('iframe')
        el.id = 'visualizer-iframe'
        el.className = 'visualizer__iframe'
        el.allow = 'fullscreen'
        // Don't set src yet; we'll attach listeners first
        slot.appendChild(el)
        iframeEl = el
      }
    }

    // Prepare target src based on detected configurator type
    let targetSrc = 'https://tcs-configurator.pages.dev'
    try {
      const type = (window.Visualizer && typeof window.Visualizer.getConfigType === 'function') ? window.Visualizer.getConfigType() : 'classic'
      // Centralized map of iframe URLs by configurator type
      const urlMap = {
        classic: 'https://tcs-configurator.pages.dev', // 'https://tcs-configurator.pages.dev',
        door: 'https://tcs-configurator.pages.dev/door',
        bookcases: 'https://tcs-configurator.pages.dev',
        carcasses: 'https://tcs-configurator.pages.dev',
        'wall-unit': 'https://tcs-configurator.pages.dev'
      }
      targetSrc = urlMap[type] || urlMap.classic
    } catch (e) {}

    // Attach listeners BEFORE setting src to avoid missing early events
    // 1) Handle configurator 'loaded' message: mark ready and send initial config
    const onMessage = (e) => {
      try {
        const dynamicOrigin = (() => { try { return new URL(targetSrc).origin } catch (_) { return null } })()
        const allowedOrigins = new Set([
          'https://tcs-configurator.pages.dev',
          'https://cabinet-shop.dev.web3dev.group',
          'https://v-0-4.cabinet-shop.dev.web3dev.group',
          'https://cabinet-shop.release.web3dev.group',
          dynamicOrigin
        ].filter(Boolean))
        if (allowedOrigins.has(e.origin) && e.data?.payload?.type === 'loaded') {
          isConfiguratorLoaded = true
          const toggles3d = document.querySelectorAll('[visualizer-id="toggle-3d"]')
          toggles3d.forEach((item) => { item.removeAttribute('disabled') })
          // Send first config exactly when configurator is fully ready
          try { Message.post(iframeEl, config, currentTab, Validation, isConfiguratorLoaded) } catch (err) { console.warn('Initial post failed:', err) }
        }
      } catch (err) {
        console.warn('onMessage handler error:', err)
      }
    }
    window.addEventListener('message', onMessage)

    // 2) Handle iframe onload separately: show viewer UI when iframe content is loaded
    if (iframeEl) {
      const onLoad = () => {
        try {
          const hiddenEls = document.querySelectorAll('[visualizer-id="hidden"]')
          hiddenEls.forEach((el) => el.classList.add('visualizer__hidden'))
          const wrapper = document.querySelector('#Slide-visualizer')
          if (wrapper) {
            wrapper.classList.remove('visualizer__wrapper_hidden')
            if (isMobileViewport()) {
              disableMobileFixed()
              updateMobileOriginalTop()
            }
            const host = wrapper.closest('.wt-product__wrapper')
            if (host) host.classList.add('visualizer--active')
          }
          isShowViewer = true
        } catch (e) {
          console.warn('Failed to show visualizer on iframe load:', e)
        }
        // keep listener single-fire
        try { iframeEl.removeEventListener('load', onLoad) } catch (e) {}
      }
      iframeEl.addEventListener('load', onLoad)
    }

    // Now set/update src to trigger loading
    try {
      if (iframeEl && iframeEl.getAttribute('src') !== targetSrc) {
        iframeEl.setAttribute('src', targetSrc)
      }
    } catch (e) {}

    // wait to load form
    await new Promise((res) => {
      const observer = new MutationObserver(() => {
        let form = document.querySelector('.dpo-container')
        if (form) {
          observer.disconnect()
          res(true)
        }
      })
      observer.observe(document.body, {
        attributes: true,
        childList: true,
        subtree: true,
      })
    })
    isFirstRender = true
    // Auto-show removed: iframe onload will handle showing the viewer now
    mountButtons()
    showButtons()
    await render()
    ensureColumnsCountSelection()
    if (typeof Options?.updateColumnInterfaceVisibility === 'function') {
      Options.updateColumnInterfaceVisibility(config)
    }

    // Save only after URL params (if any) were applied, to avoid overwriting incoming values
    if (initialUrlApplied) {
      Url.saveToUrl(config, Validation)
    }
    const debounceRender = debounce(render, 0)
    const debounceSaveToURL = debounce(() => {
      if (!initialUrlApplied && hasIncomingParams) return
      try {
        const normalizeVisualizerKey = (k) => {
          if (!k || typeof k !== 'string') return k
          if (k === 'doors-simpleOpeningDirection') return 'doors-openingDirection'
          const m = k.match(/^doors-simpleOpeningDirection-(\d+)$/)
          if (m) return `doors-openingDirection-${m[1]}`
          return k
        }
        const isColumnScopedKey = (key) => {
          if (!key || typeof key !== 'string') return false
          return (
            /^columnTemplate-\d+$/i.test(key) ||
            /^columnShelvesCount-\d+$/i.test(key) ||
            /^columnDrawersCount-\d+$/i.test(key) ||
            /^columnDividerHeight-\d+$/i.test(key) ||
            /^shelvesCount-\d+$/i.test(key) ||
            /^doors-openingDirection-\d+$/i.test(key) ||
            /^doors-simpleOpeningDirection-\d+$/i.test(key)
          )
        }
        const isHiddenColumnControl = (el) => {
          let cur = el
          while (cur && cur.nodeType === 1) {
            if (cur.classList && cur.classList.contains('visualizer-column-interface--hidden')) return true
            if (cur.hasAttribute && cur.hasAttribute('hidden')) return true
            const isSelfHiddenSliderInput =
              cur === el && cur.classList && cur.classList.contains('dpo-slider-value')
            if (!isSelfHiddenSliderInput) {
              const style = cur.style || {}
              if (style.display === 'none' || style.visibility === 'hidden' || style.opacity === '0') return true
              if (window.getComputedStyle) {
                const computed = window.getComputedStyle(cur)
                if (
                  computed &&
                  (computed.display === 'none' || computed.visibility === 'hidden' || computed.opacity === '0')
                ) {
                  return true
                }
              }
            }
            cur = cur.parentElement
          }
          return false
        }
        const getOptionSemanticValue = (inputEl) => {
          try {
            if (!inputEl) return ''
            const wrap = inputEl.closest ? inputEl.closest('.admin__field-option') : null
            const attrValue =
              (wrap && wrap.getAttribute && wrap.getAttribute('data-visualizer-value')) ||
              (inputEl.getAttribute && inputEl.getAttribute('data-visualizer-value'))
            if (attrValue !== undefined && attrValue !== null && String(attrValue).trim() !== '') {
              return String(attrValue).trim()
            }
            if (wrap) {
              const cls = Array.from(wrap.classList || []).find((c) => c.startsWith('data-visualizer-value-'))
              if (cls) {
                const fromClass = cls.replace('data-visualizer-value-', '')
                if (fromClass) return fromClass
              }
              const imageEl = wrap.querySelector('.itoris-dynamicoptions-thumbnail-image')
              if (imageEl && (imageEl.getAttribute('src') || imageEl.src)) {
                return String(imageEl.getAttribute('src') || imageEl.src)
              }
              const colorEl = wrap.querySelector('.itoris-dynamicoptions-thumbnail-color')
              if (colorEl && window.getComputedStyle) {
                const rgb = window.getComputedStyle(colorEl).getPropertyValue('background-color')
                const m = String(rgb || '').match(/^rgba?\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})(?:\s*,\s*(0|0?\.\d+|1))?\s*\)$/i)
                if (m) {
                  const toHex = (n) => Math.max(0, Math.min(255, Number(n))).toString(16).padStart(2, '0').toUpperCase()
                  return `#${toHex(m[1])}${toHex(m[2])}${toHex(m[3])}`
                }
              }
            }
            return inputEl.value
          } catch (e) {
            return inputEl && typeof inputEl.value !== 'undefined' ? inputEl.value : ''
          }
        }
        const syncPrefix = (prefix) => {
          const nodes = document.querySelectorAll(`[visualizer^="${prefix}"]`)
          nodes.forEach((el) => {
            try {
              if (!el || !el.getAttribute) return
              const rawKey = el.getAttribute('visualizer')
              const key = normalizeVisualizerKey(rawKey)
              if (!key) return
              if (isColumnScopedKey(key) && isHiddenColumnControl(el)) return
              if (typeof el.value === 'undefined') return
              config[key] = el.value
            } catch (e) { /* ignore */ }
          })
        }
        syncPrefix('shelvesCount-')
        const top = document.querySelector('[visualizer="shelvesCount"]')
        if (top && typeof top.value !== 'undefined') {
          config['shelvesCount'] = top.value
        }
        const CONFIG_TRUSTED_OPTION_KEYS = new Set(['doors-style'])
        const optionLists = Array.from(document.querySelectorAll('.options-list[visualizer]'))
        optionLists.forEach((list) => {
          try {
            const rawKey = list.getAttribute('visualizer')
            const key = normalizeVisualizerKey(rawKey)
            if (!key) return
            if (isColumnScopedKey(key) && isHiddenColumnControl(list)) return
            const checked = Array.from(list.querySelectorAll('input[type="checkbox"]:checked, input[type="radio"]:checked'))
            if (checked.length === 0) return
            if (checked.length === 1) {
              const domVal = getOptionSemanticValue(checked[0])
              if (CONFIG_TRUSTED_OPTION_KEYS.has(key) && config[key] != null && String(config[key]) !== String(domVal)) {
                const Form = global.Visualizer && global.Visualizer.Helpers && global.Visualizer.Helpers.Form
                if (Form && typeof Form.reapplyOptionsFromConfig === 'function') {
                  Form.reapplyOptionsFromConfig(config, list)
                }
                return
              }
              config[key] = domVal
            } else {
              config[key] = checked.map((i) => getOptionSemanticValue(i)).filter((v) => v !== undefined && v !== null && v !== '')
            }
          } catch (e) { /* ignore */ }
        })
        const dpoSliders = Array.from(document.querySelectorAll('input.dpo-slider-value[visualizer]'))
        dpoSliders.forEach((inp) => {
          try {
            const rawKey = inp.getAttribute('visualizer')
            const key = normalizeVisualizerKey(rawKey)
            if (!key) return
            if (isColumnScopedKey(key) && isHiddenColumnControl(inp)) return
            if (typeof inp.value === 'undefined') return
            if (isColumnScopedKey(key) && config[key] != null && String(config[key]) !== String(inp.value)) {
              const Form = global.Visualizer && global.Visualizer.Helpers && global.Visualizer.Helpers.Form
              if (Form && typeof Form.syncScalarInputDomFromConfig === 'function') {
                Form.syncScalarInputDomFromConfig(inp, key, config)
              } else {
                inp.value = String(config[key])
              }
              return
            }
            config[key] = inp.value
          } catch (e) { /* ignore */ }
        })

        const nativeRanges = Array.from(document.querySelectorAll('input[type="range"][visualizer]'))
        nativeRanges.forEach((inp) => {
          try {
            const rawKey = inp.getAttribute('visualizer')
            const key = normalizeVisualizerKey(rawKey)
            if (!key) return
            if (isColumnScopedKey(key) && isHiddenColumnControl(inp)) return
            if (typeof inp.value === 'undefined') return
            if (isColumnScopedKey(key) && config[key] != null && String(config[key]) !== String(inp.value)) {
              const Form = global.Visualizer && global.Visualizer.Helpers && global.Visualizer.Helpers.Form
              if (Form && typeof Form.syncScalarInputDomFromConfig === 'function') {
                Form.syncScalarInputDomFromConfig(inp, key, config)
              } else {
                inp.value = String(config[key])
              }
              return
            }
            config[key] = inp.value
          } catch (e) { /* ignore */ }
        })
      } catch (e) { /* non-fatal */ }
      Url.saveToUrl(config, Validation)
    }, 800)
    const observer = new MutationObserver(debounceRender)
    const urlObserver = new MutationObserver(debounceSaveToURL)
    const infoWrapper = document.querySelector('.product__info-wrapper')
    if (infoWrapper) {
      observer.observe(infoWrapper, { attributes: true, childList: true, subtree: true })
    }
    const dpoContainer = document.querySelector('.dpo-container')
    if (dpoContainer) {
      observer.observe(dpoContainer, { attributes: true, childList: true, subtree: true })
      urlObserver.observe(dpoContainer, { attributes: true, childList: true, subtree: true })
      const dimensionObserver = new MutationObserver(() => {
        initNumericInputs()
      })
      dimensionObserver.observe(dpoContainer, { childList: true, subtree: true })
    }
    // As a safety net, also save on any input change anywhere in the document
    document.addEventListener('change', debounceSaveToURL, true)

    const normalizeVisualizerKey = (k) => {
      if (!k || typeof k !== 'string') return k
      if (k === 'doors-simpleOpeningDirection') return 'doors-openingDirection'
      const m = k.match(/^doors-simpleOpeningDirection-(\d+)$/)
      if (m) return `doors-openingDirection-${m[1]}`
      return k
    }
    const getOptionSemanticValue = (inputEl) => {
      try {
        if (!inputEl) return ''
        const wrap = inputEl.closest ? inputEl.closest('.admin__field-option') : null
        const attrValue =
          (wrap && wrap.getAttribute && wrap.getAttribute('data-visualizer-value')) ||
          (inputEl.getAttribute && inputEl.getAttribute('data-visualizer-value'))
        if (attrValue !== undefined && attrValue !== null && String(attrValue).trim() !== '') {
          return String(attrValue).trim()
        }
        if (wrap) {
          const cls = Array.from(wrap.classList || []).find((c) => c.startsWith('data-visualizer-value-'))
          if (cls) {
            const fromClass = cls.replace('data-visualizer-value-', '')
            if (fromClass) return fromClass
          }
          const imageEl = wrap.querySelector('.itoris-dynamicoptions-thumbnail-image')
          if (imageEl && (imageEl.getAttribute('src') || imageEl.src)) {
            return String(imageEl.getAttribute('src') || imageEl.src)
          }
          const colorEl = wrap.querySelector('.itoris-dynamicoptions-thumbnail-color')
          if (colorEl && window.getComputedStyle) {
            const rgb = window.getComputedStyle(colorEl).getPropertyValue('background-color')
            const m = String(rgb || '').match(/^rgba?\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})(?:\s*,\s*(0|0?\.\d+|1))?\s*\)$/i)
            if (m) {
              const toHex = (n) => Math.max(0, Math.min(255, Number(n))).toString(16).padStart(2, '0').toUpperCase()
              return `#${toHex(m[1])}${toHex(m[2])}${toHex(m[3])}`
            }
          }
        }
        return inputEl.value
      } catch (e) {
        return inputEl && typeof inputEl.value !== 'undefined' ? inputEl.value : ''
      }
    }
    const syncConfigFromEvent = (event) => {
      try {
        const isColumnScopedKey = (key) => {
          if (!key || typeof key !== 'string') return false
          return (
            /^columnTemplate-\d+$/i.test(key) ||
            /^columnShelvesCount-\d+$/i.test(key) ||
            /^columnDrawersCount-\d+$/i.test(key) ||
            /^columnDividerHeight-\d+$/i.test(key) ||
            /^shelvesCount-\d+$/i.test(key) ||
            /^doors-openingDirection-\d+$/i.test(key) ||
            /^doors-simpleOpeningDirection-\d+$/i.test(key)
          )
        }
        const isHiddenColumnControl = (el) => {
          let cur = el
          while (cur && cur.nodeType === 1) {
            if (cur.classList && cur.classList.contains('visualizer-column-interface--hidden')) return true
            if (cur.hasAttribute && cur.hasAttribute('hidden')) return true
            const isSelfHiddenSliderInput =
              cur === el && cur.classList && cur.classList.contains('dpo-slider-value')
            if (!isSelfHiddenSliderInput) {
              const style = cur.style || {}
              if (style.display === 'none' || style.visibility === 'hidden' || style.opacity === '0') return true
              if (window.getComputedStyle) {
                const computed = window.getComputedStyle(cur)
                if (
                  computed &&
                  (computed.display === 'none' || computed.visibility === 'hidden' || computed.opacity === '0')
                ) {
                  return true
                }
              }
            }
            cur = cur.parentElement
          }
          return false
        }
        const el = event && event.target
        if (!el || !el.getAttribute) return
        const rawKey = el.getAttribute('visualizer')
        if (rawKey) {
          const key = normalizeVisualizerKey(rawKey)
          if (!key) return
          if (isColumnScopedKey(key) && isHiddenColumnControl(el)) return
          if (typeof el.value === 'undefined') return
          const isDpoSliderInput = el.classList && el.classList.contains('dpo-slider-value')
          if (
            isColumnScopedKey(key) &&
            isDpoSliderInput &&
            config[key] != null &&
            String(config[key]) !== String(el.value) &&
            !(event && event.isTrusted) &&
            !el.__vizAllowNextChange
          ) {
            const Form = global.Visualizer && global.Visualizer.Helpers && global.Visualizer.Helpers.Form
            if (Form && typeof Form.syncScalarInputDomFromConfig === 'function') {
              Form.syncScalarInputDomFromConfig(el, key, config)
            }
            return
          }
          config[key] = el.value
          if (key === 'columnsCount' && typeof Options?.updateColumnInterfaceVisibility === 'function') {
            Options.updateColumnInterfaceVisibility(config)
          }
          return
        }
        const list = el.closest ? el.closest('.options-list[visualizer]') : null
        if (!list) return
        const listKeyRaw = list.getAttribute('visualizer')
        const listKey = normalizeVisualizerKey(listKeyRaw)
        if (!listKey) return
        if (isColumnScopedKey(listKey) && isHiddenColumnControl(list)) return
        const checked = Array.from(list.querySelectorAll('input[type="checkbox"]:checked, input[type="radio"]:checked'))
        if (checked.length === 0) return
        config[listKey] = checked.length === 1
          ? getOptionSemanticValue(checked[0])
          : checked.map((i) => getOptionSemanticValue(i)).filter((v) => v !== undefined && v !== null && v !== '')
        if (listKey === 'columnsCount' && typeof Options?.updateColumnInterfaceVisibility === 'function') {
          Options.updateColumnInterfaceVisibility(config)
          if (typeof Form?.reapplyOptionsFromConfig === 'function') {
            Form.reapplyOptionsFromConfig(config, document, { onlyColumnScoped: true, onlyVisible: true })
          }
          if (typeof Form?.reapplyScalarInputsFromConfig === 'function') {
            Form.reapplyScalarInputsFromConfig(config, document, { onlyColumnScoped: true, onlyVisible: true })
          }
        }
        if (listKey === 'columnVisibility' && typeof Options?.updateColumnInterfaceVisibility === 'function') {
          Options.updateColumnInterfaceVisibility(config)
          if (typeof Form?.reapplyOptionsFromConfig === 'function') {
            Form.reapplyOptionsFromConfig(config, document, { onlyColumnScoped: true, onlyVisible: true })
          }
          if (typeof Form?.reapplyScalarInputsFromConfig === 'function') {
            Form.reapplyScalarInputsFromConfig(config, document, { onlyColumnScoped: true, onlyVisible: true })
          }
        }
      } catch (e) { /* non-fatal */ }
    }
    document.addEventListener('input', syncConfigFromEvent, true)
    document.addEventListener('change', syncConfigFromEvent, true)
    const hookDpoJqUiSliders = () => {
      try {
        window.__vizOnDpoSliderInput = (inp, v) => {
          try {
            if (!inp || !inp.getAttribute) return
            const rawKey = inp.getAttribute('visualizer')
            if (!rawKey) return
            const key = normalizeVisualizerKey(rawKey)
            if (!key) return
            config[key] = String(v)
            debounceSaveToURL()
          } catch (e) {
            /* ignore */
          }
        }
        if (typeof window.__vizHookDpoJqUiSliders === 'function') {
          window.__vizHookDpoJqUiSliders()
        }
      } catch (e) {
        /* non-fatal */
      }
    }
    const setupDpoCommitOnly = () => {
      if (window.__vizCommitOnlySetup) return
      window.__vizCommitOnlySetup = true
      const root = document.querySelector('.dpo-container') || document
      const commitSelector = 'input.dpo-slider-value, input[type="range"], input.dimension-slider-input'
      const resolveInput = (target) => {
        try {
          if (!target) return null
          if (target.matches && target.matches(commitSelector)) return target
          const control = target.closest && target.closest('.control')
          if (control) {
            const dpoInput = control.querySelector('input.dpo-slider-value')
            if (dpoInput) return dpoInput
          }
          const wrap = target.closest && target.closest('.dimension-slider-wrapper')
          if (wrap) {
            const dim = wrap.querySelector('input.dimension-slider-input')
            if (dim) return dim
          }
        } catch (e) { /* ignore */ }
        return null
      }
      let activeInput = null
      const startDrag = (e) => {
        const input = resolveInput(e && e.target)
        if (!input) return
        activeInput = input
        input.__vizCommitDragging = true
      }
      const endDrag = () => {
        if (!activeInput) return
        const input = activeInput
        activeInput = null
        if (!input.__vizCommitDragging) return
        input.__vizCommitDragging = false
        if (input.__vizCommitOnlySkip) {
          input.__vizCommitOnlySkip = false
          return
        }
        try {
          input.__vizAllowNextChange = true
          input.dispatchEvent(new Event('input', { bubbles: true }))
          input.dispatchEvent(new Event('change', { bubbles: true }))
        } catch (e) { /* ignore */ }
      }
      const intercept = (e) => {
        const input = resolveInput(e && e.target)
        if (!input || !input.__vizCommitDragging) return
        if (e.type === 'input' || e.type === 'change') {
          e.stopImmediatePropagation()
          e.stopPropagation()
        }
      }
      root.addEventListener('input', intercept, true)
      root.addEventListener('change', intercept, true)
      root.addEventListener('mousedown', startDrag, true)
      root.addEventListener('touchstart', startDrag, true)
      root.addEventListener('pointerdown', startDrag, true)
      window.addEventListener('mouseup', endDrag, true)
      window.addEventListener('touchend', endDrag, true)
      window.addEventListener('pointerup', endDrag, true)
      window.addEventListener('pointercancel', endDrag, true)
      window.addEventListener('touchcancel', endDrag, true)
    }
    // Skip recalculation when value didn't actually change.
    const setupDpoNoopFilter = () => {
      if (window.__vizNoopFilterSetup) return
      window.__vizNoopFilterSetup = true
      const root = document.querySelector('.dpo-container') || document
      const commitSelector = 'input.dpo-slider-value, input[type="range"], input.dimension-slider-input'
      const resolveInput = (target) => {
        try {
          if (!target) return null
          if (target.matches && target.matches(commitSelector)) return target
        } catch (e) { /* ignore */ }
        return null
      }
      const intercept = (e) => {
        const input = resolveInput(e && e.target)
        if (!input || input.__vizCommitDragging) return
        if (e.type !== 'input' && e.type !== 'change') return
        const current = String(input.value ?? '')
        if (e.type === 'change' && input.__vizAllowNextChange) {
          input.__vizAllowNextChange = false
          input.__vizLastValue = current
          return
        }
        if (input.__vizLastValue === current) {
          e.stopImmediatePropagation()
          e.stopPropagation()
          return
        }
        input.__vizLastValue = current
      }
      root.addEventListener('input', intercept, true)
      root.addEventListener('change', intercept, true)
    }
    hookDpoJqUiSliders()
    setupDpoNoopFilter()
    setupDpoCommitOnly()

    listenChangeTabs()
    bindFrontStyleWarningOnChange()
    bindHandleStyleWarningOnChange()

    // Subscribe to configurator restrictionsChanged event
    subscribeRestrictionsChanged()

    // Mobile fixed visualizer on scroll
    try {
      window.addEventListener('scroll', handleMobileScroll, { passive: true })
      window.addEventListener('resize', () => {
        if (mobileFixedActive) {
          disableMobileFixed()
        }
        mobileOriginalTop = null
        scheduleNextFrame(() => {
          updateMobileOriginalTop()
          handleMobileScroll()
        })
      })
      scheduleNextFrame(() => updateMobileOriginalTop())
    } catch (e) {}
    initNumericInputs()
  }

  function initNumericInputs() {
    const root = document.querySelector('#itoris_dynamicproductoptions') || document
    const inputsList = Array.from(
      root.querySelectorAll('input.input-text.product-custom-option[visualizer]:not(.dpo-slider-value):not([type="hidden"])')
    )

    const isNumeric = (el) => {
      if (!el) return false
      if (el.classList.contains('validate-digits')) return true
      if (el.classList.contains('validate-number')) return true
      const v = el.getAttribute('validation') || ''
      return /"number"\s*:\s*true/i.test(v) || /"digits"\s*:\s*true/i.test(v)
    }

    inputsList.forEach((input) => {
      if (!isNumeric(input)) return
      const field = input.closest('.field')
      if (!field) return
      if (field.querySelector('.dimension-slider-wrapper')) return // Already initialized
      
      const control = field.querySelector('.control')
      if (!control) return

      const hasMin = input.hasAttribute('min')
      const hasMax = input.hasAttribute('max')
      const minAttr = hasMin ? parseFloat(input.getAttribute('min')) : NaN
      const maxAttr = hasMax ? parseFloat(input.getAttribute('max')) : NaN
      const hasRange = Number.isFinite(minAttr) && Number.isFinite(maxAttr) && maxAttr > minAttr

      // Always apply the pill input style; slider is only added when range exists
      input.classList.add('dimension-slider-input')
      try { input.setAttribute('inputmode', 'numeric') } catch (e) {}

      if (!hasRange) {
        // Just wrap input to match the visual style (no slider possible)
        const box = document.createElement('div')
        box.className = 'dimension-input-box'
        box.appendChild(input)
        // Replace control contents for clean layout
        control.textContent = ''
        control.appendChild(box)
        return
      }

      let min = minAttr
      let max = maxAttr
      let step = parseFloat(input.getAttribute('step')) || 1
      const currentValue = Number.isFinite(parseFloat(input.value)) ? parseFloat(input.value) : min

      const syncRangeFromAttrs = () => {
        const minA = parseFloat(input.getAttribute('min'))
        const maxA = parseFloat(input.getAttribute('max'))
        const stepA = parseFloat(input.getAttribute('step')) || 1
        if (Number.isFinite(minA)) min = minA
        if (Number.isFinite(maxA)) max = maxA
        if (Number.isFinite(stepA) && stepA > 0) step = stepA
      }

      // Create slider wrapper
      const sliderWrapper = document.createElement('div')
      sliderWrapper.className = 'dimension-slider-wrapper'
      // Don't set `gap` inline — it should be controlled via CSS (.dimension-slider-wrapper)
      sliderWrapper.style.cssText = 'width: 100%; height: 100%; justify-content: flex-start; align-items: center; display: inline-flex'

      // Create input display (left side)
      const inputDisplay = document.createElement('div')
      inputDisplay.setAttribute('data-mob', 'no')
      inputDisplay.setAttribute('data-state', 'text')
      inputDisplay.style.cssText = 'width: 106px; flex-direction: column; justify-content: flex-start; align-items: flex-start; display: inline-flex'

      const inputContainer = document.createElement('div')
      inputContainer.setAttribute('data-state', 'Active')
      inputContainer.className = 'dimension-input-box'

      inputContainer.appendChild(input)
      inputDisplay.appendChild(inputContainer)

      // Create slider (right side)
      const sliderContainer = document.createElement('div')
      sliderContainer.className = 'dimension-slider-container'
      sliderContainer.style.cssText = 'flex: 1 1 0; position: relative; justify-content: flex-start; align-items: center; display: flex'

      const sliderTrack = document.createElement('div')
      sliderTrack.className = 'dimension-slider-track'
      const handleWidth = 62 // handle width in pixels
      const handleHalfWidth = handleWidth / 2 // half of the handle width
      sliderTrack.style.cssText = 'flex: 1 1 0; height: 2px; background: #B8B8B8; border-radius: 16px; position: relative'

      const sliderProgress = document.createElement('div')
      sliderProgress.className = 'dimension-slider-progress'
      const progressPercent = (max > min) ? ((currentValue - min) / (max - min)) * 100 : 0
      sliderProgress.style.cssText = `width: ${progressPercent}%; height: 2px; position: absolute; left: 0; top: 0; background: #121212; border-radius: 16px`

      const sliderHandle = document.createElement('div')
      sliderHandle.className = 'dimension-slider-handle'
      sliderHandle.setAttribute('data-state', 'Default')
      sliderHandle.setAttribute('data-type', 'handle')
      sliderHandle.style.cssText = `height: 32px; width: ${handleWidth}px; left: ${progressPercent}%; top: -15px; position: absolute; transform: translateX(-50%); justify-content: flex-start; align-items: flex-start; display: flex; cursor: grab; z-index: 2`
      sliderHandle.innerHTML = `
        <div style="width: ${handleWidth}px; height: 100%; padding-left: 8px; padding-right: 8px; padding-top: 8px; padding-bottom: 8px; background: #121212; border-radius: 36px; justify-content: center; align-items: center; gap: 8px; display: flex; box-sizing: border-box">
          <div style="justify-content: center; align-items: center; display: flex; width: 100%">
            <div class="dimension-slider-handle-value" style="justify-content: center; display: flex; flex-direction: column; color: white; font-size: 12px; font-family: Montserrat; font-weight: 600; line-height: 16.80px; word-wrap: break-word; text-align: center">${currentValue}</div>
          </div>
        </div>
      `

      sliderTrack.appendChild(sliderProgress)
      sliderTrack.appendChild(sliderHandle)
      sliderContainer.appendChild(sliderTrack)

      sliderWrapper.appendChild(inputDisplay)
      sliderWrapper.appendChild(sliderContainer)

      // Replace control contents for clean layout
      control.textContent = ''
      control.appendChild(sliderWrapper)

      let suppressInputChange = false
      let pendingInputDispatch = false
      let lastCommittedValue = String(currentValue)

      let pendingRecalc = false
      const scheduleRecalc = () => {
        if (pendingRecalc) return
        pendingRecalc = true
        requestAnimationFrame(() => {
          pendingRecalc = false
          updateSlider(input.value, { emitEvents: false })
        })
      }

      const clamp = (v, minV, maxV) => Math.max(minV, Math.min(maxV, v))

      const dispatchInputAndMaybeChange = ({ commit } = {}) => {
        try {
          if (commit) input.__vizAllowNextChange = true
          input.dispatchEvent(new Event('input', { bubbles: true }))
          if (commit) {
            input.dispatchEvent(new Event('change', { bubbles: true }))
            lastCommittedValue = String(input.value)
            input.__vizCommitOnlySkip = true
          }
        } catch (e) { /* ignore */ }
      }

      const scheduleInputDispatch = ({ commit } = {}) => {
        if (commit) return dispatchInputAndMaybeChange({ commit: true })
        if (pendingInputDispatch) return
        pendingInputDispatch = true
        requestAnimationFrame(() => {
          pendingInputDispatch = false
          dispatchInputAndMaybeChange({ commit: false })
        })
      }

      const updateSlider = (value, { emitEvents = true, commit = false } = {}) => {
        syncRangeFromAttrs()
        const safeRange = (Number.isFinite(min) && Number.isFinite(max) && max > min) ? (max - min) : 0
        const numValue = safeRange > 0 ? Math.max(min, Math.min(max, parseFloat(value) || min)) : (parseFloat(value) || 0)
        const percent = safeRange > 0 ? ((numValue - min) / safeRange) * 100 : 0
        const percentClamped = clamp(percent, 0, 100)

        const rect = sliderTrack.getBoundingClientRect()

        if (!rect.width || rect.width <= handleWidth) {
          sliderProgress.style.width = `${percentClamped}%`
          scheduleRecalc()
        } else {
          const usableWidth = rect.width - handleWidth
          const handleLeft = handleHalfWidth + (percentClamped / 100) * usableWidth
          const handleLeftPercent = clamp((handleLeft / rect.width) * 100, 0, 100)

          sliderProgress.style.width = `${percentClamped}%`
          sliderHandle.style.left = `${handleLeftPercent}%`
        }

        const roundedValue = Math.round(numValue)
        const handleValue = sliderHandle.querySelector('.dimension-slider-handle-value')
        if (handleValue) handleValue.textContent = roundedValue

        // Only emit events when the slider UI initiated the update.
        if (!emitEvents) return
        if (String(input.value) === String(numValue)) return
        suppressInputChange = true
        input.value = numValue
        scheduleInputDispatch({ commit: !!commit })
        suppressInputChange = false
      }

      // Slider drag functionality
      let isDragging = false
      const handleDrag = (clientX) => {
        syncRangeFromAttrs()
        const rect = sliderTrack.getBoundingClientRect()
        const x = clientX - rect.left
        const usableWidth = rect.width - handleWidth
        if (!Number.isFinite(usableWidth) || usableWidth <= 0) return
        const xInUsableArea = x - handleHalfWidth
        const percent = Math.max(0, Math.min(100, (xInUsableArea / usableWidth) * 100))
        const range = (Number.isFinite(min) && Number.isFinite(max) && max > min) ? (max - min) : 0
        const value = min + (percent / 100) * range
        updateSlider(Math.round(value / step) * step, { emitEvents: true, commit: false })
      }

      sliderHandle.addEventListener('mousedown', (e) => {
        isDragging = true
        sliderHandle.style.cursor = 'grabbing'
        e.preventDefault()
      })

      sliderHandle.addEventListener('touchstart', (e) => {
        isDragging = true
        e.preventDefault()
      })

      const handleMove = (e) => {
        if (!isDragging) return
        const clientX = e.touches ? e.touches[0].clientX : e.clientX
        handleDrag(clientX)
      }

      const handleEnd = () => {
        if (isDragging) {
          isDragging = false
          sliderHandle.style.cursor = 'grab'
          try {
            if (String(input.value) !== String(lastCommittedValue)) {
              scheduleInputDispatch({ commit: true })
            }
          } catch (e) { /* ignore */ }
        }
      }

      document.addEventListener('mousemove', handleMove)
      document.addEventListener('mouseup', handleEnd)
      document.addEventListener('touchmove', handleMove, { passive: false })
      document.addEventListener('touchend', handleEnd)

      // Click on track
      sliderTrack.addEventListener('click', (e) => {
        if (e.target === sliderHandle || sliderHandle.contains(e.target)) return
        syncRangeFromAttrs()
        const rect = sliderTrack.getBoundingClientRect()
        const x = e.clientX - rect.left
        const usableWidth = rect.width - handleWidth
        if (!Number.isFinite(usableWidth) || usableWidth <= 0) return
        const xInUsableArea = x - handleHalfWidth
        const percent = Math.max(0, Math.min(100, (xInUsableArea / usableWidth) * 100))
        const range = (Number.isFinite(min) && Number.isFinite(max) && max > min) ? (max - min) : 0
        const value = min + (percent / 100) * range
        updateSlider(Math.round(value / step) * step, { emitEvents: true, commit: true })
      })

      try {
        if (typeof MutationObserver !== 'undefined') {
          const mo = new MutationObserver(() => {
            updateSlider(input.value, { emitEvents: false })
          })
          mo.observe(input, { attributes: true, attributeFilter: ['min', 'max', 'step'] })
        }
      } catch (e) { }

      // Sync UI when user types in the real input
      input.addEventListener('input', () => {
        if (suppressInputChange) return
        updateSlider(input.value, { emitEvents: false })
      })

      // Sync UI on committed changes as well (blur / enter)
      input.addEventListener('change', () => {
        if (suppressInputChange) return
        updateSlider(input.value, { emitEvents: false })
      })

      try {
        if (typeof ResizeObserver !== 'undefined') {
          const ro = new ResizeObserver(() => {
            updateSlider(input.value, { emitEvents: false })
          })
          ro.observe(sliderTrack)
        } else {
          window.addEventListener('resize', () => updateSlider(input.value, { emitEvents: false }))
        }
      } catch (e) { }

      requestAnimationFrame(() => {
        updateSlider(currentValue, { emitEvents: false })
      })
    })
  }

  const configuratorDetected = await waitForConfiguratorMarkers()
  if (!configuratorDetected) {
    teardownVisualizerUI()
    return
  }
  showButtons()

  await mount()
}
visualize()
